import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { HubConnectionFactory } from './hub-connection.factory';
import { SignalRHubNames } from './signalr-hub-names';
import { ConversationRealtimeSummary } from '../../features/conversations/conversations.models';

export interface ConversationTakenOverEvent {
  conversationId: string;
  previousOwnerUserId: string | null;
  newOwnerUserId: string;
}

export interface ConversationReadEvent {
  conversationId: string;
  readByUserId: string;
}

export interface MessageReceivedEvent {
  conversationId: string;
  messageId: string;
  preview: string;
}

export interface MessageStatusUpdatedEvent {
  conversationId: string;
  messageId: string;
  status: string;
}

/**
 * Connection lifecycle plus the six server-to-client events (Sprint 2B Section 9) for
 * ConversationsHub: ConversationCreated, ConversationUpdated, ConversationTakenOver,
 * ConversationRead, MessageReceived, MessageStatusUpdated. Every event is broadcast to the
 * caller's org group only -- see SignalRConversationNotifier's own remarks on the backend.
 */
@Injectable({ providedIn: 'root' })
export class ConversationsHubService {
  private readonly connection: HubConnection = inject(HubConnectionFactory).create(SignalRHubNames.conversations);

  private readonly conversationCreatedSubject = new Subject<ConversationRealtimeSummary>();
  private readonly conversationUpdatedSubject = new Subject<ConversationRealtimeSummary>();
  private readonly conversationTakenOverSubject = new Subject<ConversationTakenOverEvent>();
  private readonly conversationReadSubject = new Subject<ConversationReadEvent>();
  private readonly messageReceivedSubject = new Subject<MessageReceivedEvent>();
  private readonly messageStatusUpdatedSubject = new Subject<MessageStatusUpdatedEvent>();

  readonly conversationCreated$ = this.conversationCreatedSubject.asObservable();
  readonly conversationUpdated$ = this.conversationUpdatedSubject.asObservable();
  readonly conversationTakenOver$ = this.conversationTakenOverSubject.asObservable();
  readonly conversationRead$ = this.conversationReadSubject.asObservable();
  readonly messageReceived$ = this.messageReceivedSubject.asObservable();
  readonly messageStatusUpdated$ = this.messageStatusUpdatedSubject.asObservable();

  constructor() {
    this.connection.on('ConversationCreated', (payload: ConversationRealtimeSummary) => this.conversationCreatedSubject.next(payload));
    this.connection.on('ConversationUpdated', (payload: ConversationRealtimeSummary) => this.conversationUpdatedSubject.next(payload));
    this.connection.on('ConversationTakenOver', (payload: ConversationTakenOverEvent) => this.conversationTakenOverSubject.next(payload));
    this.connection.on('ConversationRead', (payload: ConversationReadEvent) => this.conversationReadSubject.next(payload));
    this.connection.on('MessageReceived', (payload: MessageReceivedEvent) => this.messageReceivedSubject.next(payload));
    this.connection.on('MessageStatusUpdated', (payload: MessageStatusUpdatedEvent) => this.messageStatusUpdatedSubject.next(payload));
  }

  async start(): Promise<void> {
    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connection.start();
    }
  }

  async stop(): Promise<void> {
    await this.connection.stop();
  }
}
